import React from "react";
import { QuickAddContainer, SecurityName } from "@bloomberg/media-ui-markets";
import { classes } from "@bloomberg/media-ui-core";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import { useFeatureGate } from "@foundation/statsig";
import typographyStyles from "@bloomberg/media-ui-tokens/css/typography.module.css";
import styles from "./quotePageHeader.module.css";
import { getSecurityName } from "~utils/securityUtils";
import MarketStatus from "~components/MarketStatus";
import { useLocale } from "~hooks/i18n/useLocale";

export type QuotePageHeaderProps = {
  isOpen: boolean | null;
  isExchangeDelayed: boolean | null;
  lastUpdate: string | null;
  longName: string | null;
  shortName: string | null;
  type: string | null;
  id: string | null;
  primaryExchange: string | null;
  issuedCurrency: string | null;
  mediaSecurityType: string;
  companyIsPrivate: boolean;
};

type ExchangeList = { [index: string]: string };

const WATCHLIST_TYPES = new Set([
  "Index",
  "Fund",
  "Currency",
  "Commodity",
  "Bond",
  "Company",
]);

const EXCHANGE_OVERRIDES: ExchangeList = {
  "A2X STOCK EX": "A2X Stock Ex",
  "BOVESPA OUTC": "Bovespa OUTC",
  "CSE2 - CANADIAN SECURITES EXCHANGE": "CSE2 - Canadian Securities Exchange",
  "EURO OTC": "Euro OTC",
  MEXDER: "MexDer",
  "MIAX PEARL": "MIAX Pearl",
  "NASDAQ CM": "Nasdaq CM",
  "NASDAQ CXD Toronto": "Nasdaq CXD Toronto",
  "NASDAQ CXD Ventures": "Nasdaq CXD Ventures",
  "NASDAQ Futures Exchange": "Nasdaq Futures Exchange",
  "NASDAQ GM": "Nasdaq GM",
  "NASDAQ GS": "Nasdaq GS",
  "NASDAQ iM": "Nasdaq iM",
  "NASDAQ OMX Commodities": "Nasdaq OMX Commodities",
  "NASDAQ OMX PHLX": "Nasdaq OMX PHLX",
  "NASDAQ OMX Swedish FI Derivs": "Nasdaq OMX Swedish FI Derivs",
  "NYSE ARCA Eu": "NYSE Arca Eu",
  "RIO DE JANER": "Rio de Janero",
  "SIGMA X Canada": "Sigma X Canada",
  "SIGMA X MTF": "Sigma X MTF",
  "SIGMA-X EU": "Sigma X EU",
  "Turks& Caico": "Turks & Caicos",
};

function formatExchange(exchange: string | null): string {
  if (!exchange) {
    return "";
  }
  const wordsInExchange = exchange.split(" ");
  const capitalizedWords = wordsInExchange.map((word) => {
    const firstLetter = word.charAt(0);
    const remainingLetters = word.substring(1);

    return `${firstLetter.toUpperCase()}${remainingLetters.toLowerCase()}`;
  });

  return capitalizedWords.join(" ");
}

function trackQuickAddClick(eventAction: string, eventLabel: string) {
  trackClickEvent("Watchlist", eventLabel, eventAction, "Watchlist");
}

export default function QuotePageHeader(props: QuotePageHeaderProps) {
  const {
    isOpen,
    isExchangeDelayed,
    lastUpdate,
    longName,
    shortName,
    type,
    mediaSecurityType,
    companyIsPrivate,
    id,
    primaryExchange,
    issuedCurrency,
  } = props;
  const featureGateEnabled = Boolean(
    useFeatureGate("watchlist_paywall")?.value ?? false
  );
  const { isJapanese } = useLocale();

  if (
    !isOpen &&
    !isExchangeDelayed &&
    !lastUpdate &&
    !longName &&
    !shortName &&
    !id &&
    !primaryExchange &&
    !issuedCurrency
  ) {
    return null;
  }

  const formattedExchange =
    (primaryExchange && EXCHANGE_OVERRIDES[primaryExchange]) ||
    formatExchange(primaryExchange);

  const securityDetails = formattedExchange ? (
    <span className={styles.securityDetails}>
      {formattedExchange} {issuedCurrency && `(${issuedCurrency})`}
    </span>
  ) : (
    issuedCurrency && (
      <span className={styles.securityDetails}>{`(${issuedCurrency})`}</span>
    )
  );

  const upperCaseId = id?.toUpperCase() || "";
  const securityName = getSecurityName(shortName, longName, upperCaseId, type);
  const canAddSecurityToWatchlist =
    WATCHLIST_TYPES.has(mediaSecurityType) && !companyIsPrivate;
  const showWatchlist = canAddSecurityToWatchlist && id && !isJapanese;

  return (
    <div className={styles.quotePageHeader}>
      {securityName && id && (
        <h1>
          <div className={styles.securityNameWrap}>
            <SecurityName
              className={styles.securityName}
              size="extraLarge"
              securityName={securityName}
            />
            {showWatchlist && (
              <QuickAddContainer
                disabled={!featureGateEnabled}
                trackClick={trackQuickAddClick}
                className={classes([styles.quickAddContainer, styles.desktop])}
                ticker={id}
              />
            )}
          </div>
          <div className={styles.watchlistIdWrap}>
            <SecurityName
              className={classes([styles.securityName, styles.tickerSymbol])}
              size="large"
              securityName={upperCaseId}
            />
            {showWatchlist && (
              <QuickAddContainer
                disabled={!featureGateEnabled}
                trackClick={trackQuickAddClick}
                className={classes([styles.quickAddContainer, styles.mobile])}
                ticker={id}
              />
            )}
          </div>
        </h1>
      )}
      <div>
        {securityDetails}
        <MarketStatus
          className={typographyStyles.coreShortTextMd}
          isOpen={isOpen}
        />
      </div>
    </div>
  );
}
